import React, { useState, useRef } from 'react';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { getImageUrl } from '../config/runtime';

/**
 * ObservationCard - Displays a detection/observation from ThingsDB
 * 
 * Used in list views to show individual discoveries made by users.
 * Includes image with bounding box overlay, confidence badge, and user info.
 */
const ObservationCard = ({ 
  observation, 
  onClick,
  isNearEnd = false,
  observerRef = null,
}) => {
  const rawImageUrl = observation.sample_images?.[0] || null;
  const displayName = observation.canonical_name || observation.label || 'Unknown discovery';
  const confidence = observation.confidence ? Math.round(observation.confidence * 100) : null;
  const capturedAt = observation.last_seen_at ? new Date(observation.last_seen_at) : null;
  const [imageDims, setImageDims] = useState(null);
  const containerRef = useRef(null);
  
  // Determine the appropriate Cloudflare image variant based on viewport
  const getImageVariant = () => {
    if (typeof window === 'undefined') return null;
    const width = window.innerWidth;
    if (width < 768) return 'cardmobile';
    if (width < 1280) return 'cardtablet';
    return 'carddesktop';
  };
  
  const imageUrl = rawImageUrl ? getImageUrl(rawImageUrl, getImageVariant()) : null;
  
  // Get the primary bbox (from bbox or first item in bboxes)
  const bbox = observation.bbox || (observation.bboxes?.[0]) || null;
  
  // Calculate bbox overlay style accounting for object-cover crop
  const getBboxStyle = () => {
    if (!bbox || !imageDims || !containerRef.current) return null;
    const { x1, y1, x2, y2 } = bbox;
    const { naturalWidth, naturalHeight } = imageDims;
    
    if (!naturalWidth || !naturalHeight) return null;
    
    // Get container dimensions (square)
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerSize = containerRect.width; // Square, so width = height
    
    // Calculate how object-cover scales and positions the image
    const imageAspect = naturalWidth / naturalHeight;
    const containerAspect = 1; // Square
    
    let scale, offsetX, offsetY;
    
    if (imageAspect > containerAspect) {
      // Image is wider - height fills container, width is cropped
      scale = containerSize / naturalHeight;
      const scaledWidth = naturalWidth * scale;
      offsetX = (scaledWidth - containerSize) / 2;
      offsetY = 0;
    } else {
      // Image is taller - width fills container, height is cropped
      scale = containerSize / naturalWidth;
      const scaledHeight = naturalHeight * scale;
      offsetX = 0;
      offsetY = (scaledHeight - containerSize) / 2;
    }
    
    // Transform bbox coordinates to container space
    const left = (x1 * scale - offsetX) / containerSize * 100;
    const top = (y1 * scale - offsetY) / containerSize * 100;
    const width = ((x2 - x1) * scale) / containerSize * 100;
    const height = ((y2 - y1) * scale) / containerSize * 100;
    
    // Skip if bbox covers nearly the whole image (>85%) - not useful to show
    if (width > 85 && height > 85) return null;
    
    // Skip if bbox is completely outside visible area
    if (left + width < 0 || left > 100 || top + height < 0 || top > 100) return null;
    
    // Clamp to visible area
    const clampedLeft = Math.max(0, left);
    const clampedTop = Math.max(0, top);
    const clampedWidth = Math.min(100 - clampedLeft, width - (clampedLeft - left));
    const clampedHeight = Math.min(100 - clampedTop, height - (clampedTop - top));
    
    // Skip if resulting box is too small after clamping
    if (clampedWidth < 5 || clampedHeight < 5) return null;
    
    return {
      left: `${clampedLeft}%`,
      top: `${clampedTop}%`,
      width: `${clampedWidth}%`,
      height: `${clampedHeight}%`,
    };
  };
  
  const bboxStyle = getBboxStyle();
  
  return (
    <div
      ref={isNearEnd ? observerRef : null}
      className="group cursor-pointer"
      onClick={() => onClick?.(observation)}
    >
      {/* Image */}
      <div 
        ref={containerRef}
        className="relative aspect-square overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800"
      >
        {rawImageUrl ? (
          <img
            src={imageUrl}
            srcSet={`
              ${getImageUrl(rawImageUrl, 'cardmobile')} 400w,
              ${getImageUrl(rawImageUrl, 'cardtablet')} 700w,
              ${getImageUrl(rawImageUrl, 'carddesktop')} 800w
            `}
            sizes="(max-width: 767px) 50vw, (max-width: 1279px) 33vw, 20vw"
            alt={displayName}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onLoad={(e) => setImageDims({
              naturalWidth: e.target.naturalWidth,
              naturalHeight: e.target.naturalHeight
            })}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl">
            🔍
          </div>
        )}
        {/* Bounding box overlay */}
        {bboxStyle && (
          <div 
            className="absolute border-2 border-green-400/80 rounded pointer-events-none shadow-sm"
            style={bboxStyle}
          />
        )}
        {/* Confidence badge */}
        {confidence && (
          <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
            {confidence}%
          </div>
        )}
        {/* Category badge */}
        {observation.category && (
          <div className="absolute top-2 left-2 bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white text-xs px-2 py-1 rounded-full capitalize">
            {observation.category}
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="pt-3">
        <h3 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-1">
          {displayName}
        </h3>
        
        {/* User who found it and date */}
        <div className="flex items-center gap-1.5 mt-1">
          {observation.user_avatar ? (
            <img
              src={getImageUrl(observation.user_avatar)}
              alt={observation.user_display || observation.username || 'User'}
              className="w-4 h-4 rounded-full object-cover"
            />
          ) : (
            <UserCircleIcon className="w-4 h-4 text-gray-400" />
          )}
          <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
            {observation.user_display || observation.username || 'Anonymous'}
          </span>
          {capturedAt && (
            <>
              <span className="text-gray-400">·</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {capturedAt.toLocaleDateString()}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObservationCard;

